// @copyright  2006-2026 West Indies Software Engineering Company Limited. All rights reserved.

/*
 * Practice-management screens sit under the organisation-scoped tree like every
 * other domain, with one deliberate exception: the prospect profile form. The
 * prospect has no organisation — and may arrive with no session; the page
 * itself asks for one on a live token (onboarding-04) while dead links must
 * still render without one, which is why the meta stays `loginRequired: false`
 * — so its route is absolute and is listed last, apart from the rest.
 *
 * Every route except the prospect form carries its screen's real permission
 * code — the code the screen's own API calls require, mirrored so
 * `beforeEachRoute` can refuse the wrong audience before a dead screen loads.
 * The prospect route alone keeps `permission: '*'`: its visitor holds no
 * membership at all. The backend remains the authority on who may see what; a
 * meta is never broader than the API's own permission.
 */

export default [
    {
        path: '/:organisation/practice-management/clients',
        component: () => import(/* webpackChunkName: "practice-management" */ '@/pages/[organisation]/practice-management/clients/index.vue'),
        name: 'practice-management.clients',
        meta: {
            guestRequired: false,
            loginRequired: true,
            permission: 'access.practice-management.engagement.search-engagements',
        },

        /*
         * The clients surface is a master–detail frame, the invoices pattern:
         * the parent renders the list in a `content-navigator` and each child
         * fills the content area beside it. The child PATHS and NAMES are the
         * ones the standalone pages carried — deep links and the E2E suites
         * depend on both staying put.
         */
        children: [
            {
                path: ':engagementId/review',
                component: () => import(/* webpackChunkName: "practice-management" */ '@/pages/[organisation]/practice-management/clients/[engagementId]/review.vue'),
                name: 'practice-management.review',
                meta: {
                    guestRequired: false,
                    loginRequired: true,
                    permission: 'access.practice-management.engagement.view-engagement',
                },
            },
            /*
             * One task, beside the client list (`FR-10`, 2026-08-13). Declared
             * HERE — above the bare `:engagementId` — for the same reason
             * `review` is: `:engagementId` matches any single segment, and a
             * child declared after it would be shadowed by the engagement route
             * for every URL that reaches this depth. The static `tasks` segment
             * is what distinguishes the two, and it only gets the chance to
             * match if it is offered first.
             */
            {
                path: ':engagementId/tasks/:taskId',
                component: () => import(/* webpackChunkName: "practice-management" */ '@/pages/[organisation]/practice-management/clients/[engagementId]/tasks/[taskId].vue'),
                name: 'practice-management.task',
                meta: {
                    guestRequired: false,
                    loginRequired: true,
                    permission: 'access.practice-management.task.view-practice-task',
                },
            },
            {
                path: ':engagementId',
                component: () => import(/* webpackChunkName: "practice-management" */ '@/pages/[organisation]/practice-management/clients/[engagementId].vue'),
                meta: {
                    guestRequired: false,
                    loginRequired: true,
                    permission: 'access.practice-management.engagement.view-engagement',
                },

                /*
                 * THE ENGAGEMENT DETAIL IS A TABBED HUB (user ruling,
                 * 2026-08-14), on `BankAccountLayout`'s pattern rather than the
                 * invoices detail's — an invoice detail is one bounded record,
                 * a bank account and a practice client are both a hub in front
                 * of several unbounded lists.
                 *
                 * The tabs render into a NAMED view (`tab`) and the slide-outs
                 * into the default one, because the two must be able to show at
                 * once: a route that supplied only the default view would blank
                 * the panel behind the slide-out. So `new-task` and `edit-task`
                 * each name the Tasks tab as well — they are task-space, and
                 * that is the panel a practice expects to see them over.
                 */
                children: [
                    /*
                     * `practice-management.engagement` NAMES THE REDIRECT, not
                     * the hub. Nine places push this name — the client list, the
                     * compliance board, the review screen, both task slide-outs,
                     * the task detail — and a name on the PARENT is one Vue
                     * Router resolves to the parent alone: the hub rendered its
                     * header and its tab rail over a `tab` view that matched
                     * nothing, which is a client screen with no client work on
                     * it. Naming the empty-path child instead means the name and
                     * the address behave identically, both landing on Tasks, and
                     * every existing caller keeps working untouched.
                     */
                    {
                        path: '',
                        name: 'practice-management.engagement',
                        redirect: (to) => ({ name: 'practice-management.engagement.tasks', params: to.params }),
                    },
                    {
                        path: 'tasks',
                        components: { tab: () => import(/* webpackChunkName: "practice-management" */ '@/pages/[organisation]/practice-management/clients/[engagementId]/tabs/tasks.vue') },
                        name: 'practice-management.engagement.tasks',
                        meta: {
                            guestRequired: false,
                            loginRequired: true,
                            permission: 'access.practice-management.engagement.view-engagement',
                        },
                    },
                    {
                        path: 'filings',
                        components: { tab: () => import(/* webpackChunkName: "practice-management" */ '@/pages/[organisation]/practice-management/clients/[engagementId]/tabs/filings.vue') },
                        name: 'practice-management.engagement.filings',
                        meta: {
                            guestRequired: false,
                            loginRequired: true,
                            permission: 'access.practice-management.engagement.view-engagement',
                        },
                    },
                    {
                        path: 'details',
                        components: { tab: () => import(/* webpackChunkName: "practice-management" */ '@/pages/[organisation]/practice-management/clients/[engagementId]/tabs/details.vue') },
                        name: 'practice-management.engagement.details',
                        meta: {
                            guestRequired: false,
                            loginRequired: true,
                            permission: 'access.practice-management.engagement.view-engagement',
                        },
                    },
                    {
                        path: 'new-task',
                        components: {
                            default: () => import(/* webpackChunkName: "practice-management" */ '@/pages/[organisation]/practice-management/clients/[engagementId]/new-task.vue'),
                            tab: () => import(/* webpackChunkName: "practice-management" */ '@/pages/[organisation]/practice-management/clients/[engagementId]/tabs/tasks.vue'),
                        },
                        name: 'practice-management.new-task',
                        meta: {
                            guestRequired: false,
                            loginRequired: true,
                            permission: 'access.practice-management.task.create-practice-task',
                        },
                    },
                    /*
                     * Editing is the same panel as creating (user ruling,
                     * 2026-08-14) — and, being a route, it fetches the single
                     * task on the way in, which is how the questions reach the
                     * form at all. The segment is `edit-task` rather than
                     * `tasks/:taskId/edit`: the parent-level `:engagementId/tasks/:taskId`
                     * detail route is matched first and would claim any address
                     * whose second segment is the literal `tasks`.
                     */
                    {
                        path: 'edit-task/:taskId',
                        components: {
                            default: () => import(/* webpackChunkName: "practice-management" */ '@/pages/[organisation]/practice-management/clients/[engagementId]/edit-task.vue'),
                            tab: () => import(/* webpackChunkName: "practice-management" */ '@/pages/[organisation]/practice-management/clients/[engagementId]/tabs/tasks.vue'),
                        },
                        name: 'practice-management.edit-task',
                        meta: {
                            guestRequired: false,
                            loginRequired: true,
                            permission: 'access.practice-management.task.update-practice-task',
                        },
                    },
                ],
            },
        ],
    },
    {
        path: '/:organisation/practice-management/compliance',
        component: () => import(/* webpackChunkName: "practice-management" */ '@/pages/[organisation]/practice-management/compliance/index.vue'),
        name: 'practice-management.compliance',
        meta: {
            guestRequired: false,
            loginRequired: true,
            permission: 'access.practice-management.compliance.view-compliance-dashboard',
        },
    },
    {
        path: '/client-onboarding/:token',
        component: () => import(/* webpackChunkName: "guests" */ '@/pages/guest/client-onboarding/[token].vue'),
        name: 'practice-management.prospect-profile',
        meta: {
            guestRequired: false,
            loginRequired: false,
            permission: '*',
        },
    },
];
